import { mayLeavePage } from "../navigation/leaveGuard";
import {
  Box,
  Flex,
  Icon,
  IconButton,
  Image,
  useColorMode,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMenu } from "react-icons/fi";
import { Link as RouterLink, Outlet } from "react-router-dom";
import { assets } from "../../assets/assets";
import { urls } from "../../urls";
import { AssistantPanelTrigger } from "../agent/AssistantPanelTrigger";
import { NotificationBell } from "../notifications/NotificationBell";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { VerifyEmailBanner } from "./VerifyEmailBanner";
import { type CreateModals, Sidebar } from "./Sidebar";
import { SkipToMainLink } from "./SkipToMainLink";
import { MAIN_REGION_ID, useRegionToggle } from "./useRegionToggle";
import { useFocusFirstRow } from "./useFocusFirstRow";
import { useAppRole } from "./useAppRole";
import { useNavBadges } from "./useNavBadges";
import { useMountsShell } from "./pageFrame";
import { WORK_AREA_PADDING } from "./workAreaPadding";
import { besidePanelWidthPx, usePanelLayout } from "./panelLayoutContext";

export const AppShell = ({ createModals }: { createModals: CreateModals }) => {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const role = useAppRole();
  const badges = useNavBadges();
  const { placement } = usePanelLayout();
  useMountsShell();
  useRegionToggle();
  useFocusFirstRow();

  return (
    /* Fixed + inset rather than `h="100vh"`: a 100vh box still sits in the document flow, so anything
       that makes the document taller than the viewport — a horizontal scrollbar eating into `vh`, a
       portal child laid out after the shell — lets the wheel scroll the whole app out of the window.
       Out of flow, the shell always spans exactly the visible area and the window has nothing to scroll. */
    <Flex position="fixed" inset="0" overflow="hidden">
      <SkipToMainLink />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        role={role}
        badges={badges}
        createModals={createModals}
      />
      <Flex
        direction="column"
        flex="1"
        minW="0"
        overflow="hidden"
        mr={`${besidePanelWidthPx(placement)}px`}
        transition="margin-right 0.2s ease"
      >
        <ImpersonationBanner />
        <VerifyEmailBanner />
        {/* Mobile-only top bar: the hamburger + logo + bell. On desktop the sidebar is always
            visible and carries the bell next to the logo, so no top bar is rendered. */}
        <Flex
          display={{ base: "flex", lg: "none" }}
          align="center"
          gap="3"
          h="56px"
          px="4"
          flexShrink={0}
          bg="smBackground"
          borderBottom="1px solid"
          borderColor="smBorder"
        >
          <IconButton
            aria-label={t("Open menu")}
            icon={<Icon as={FiMenu} />}
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
          />
          <RouterLink
            to={urls.app.home}
            onClick={(event) => {
              if (!mayLeavePage()) event.preventDefault();
            }}
          >
            <Image
              src={colorMode === "dark" ? assets.logo_dark : assets.logo}
              alt="SourceMagnet"
              h="24px"
            />
          </RouterLink>
          <Flex ml="auto" align="center">
            <AssistantPanelTrigger />
            <NotificationBell />
          </Flex>
        </Flex>
        <Box
          as="main"
          id={MAIN_REGION_ID}
          tabIndex={-1}
          aria-label={t("Work area") ?? undefined}
          outline="none"
          flex="1"
          overflowY="auto"
          overscrollBehavior="contain"
          bg="smBackgroundSecondary"
          p={WORK_AREA_PADDING}
        >
          <Outlet />
        </Box>
      </Flex>
    </Flex>
  );
};
